import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiPatch } from "../lib/apiClient.js";
import { callLlm } from "../lib/forecast/llmClient.js";
import { reportFailure } from "../lib/failureCounts.js";
import { withIntent } from "../lib/telemetry/instrument";
import { emitStackArrange } from "../lib/telemetry/uiIntents.js";
import {
  cleanStackName,
  moveIntoStack,
  renameStack,
  reorderInStack,
  promoteInStack,
  sanitizeStacks,
  stackCards,
  stackOf,
  stackTheme,
  unstackCard,
} from "../lib/forecast/stacks.js";

/** How long a name suggestion may take before the quick guess stands. */
const SUGGEST_TIMEOUT_MS = 8000;

const suggestPrompt = (questions) =>
  "These forecasting questions were grouped together because they bear on one decision.\n" +
  "Reply with ONLY a 1-3 word name for the group's common theme, in Title Case, no quotes.\n\n" +
  questions.map((q) => `- ${q}`).join("\n");

/**
 * Forecast stacks (lib/forecast/stacks.js), saved per account in
 * users.preferences.forecastStacks — the same optimistic-update-then-debounced-PATCH pattern
 * as useForecastOrder. Also holds view state that is not saved: which stack is open (one at a
 * time) and which is being named.
 *
 * A new stack is named at once from the words its questions share, then from a model's
 * suggestion if one arrives before the user types; the name field stays open either way.
 *
 * @param {{ preferences: object, setPreferences: Function, enabled: boolean }} deps
 */
export function useForecastStacks({ preferences, setPreferences, enabled }) {
  const stacks = useMemo(
    () => (enabled ? sanitizeStacks(preferences?.forecastStacks) : []),
    [enabled, preferences],
  );
  const stacksRef = useRef(stacks);
  useEffect(() => {
    stacksRef.current = stacks;
  }, [stacks]);
  const [expanded, setExpanded] = useState(() => new Set());
  const [editingId, setEditingId] = useState(null);
  // Stacks whose name the user has typed into: a late suggestion must not overwrite them. A set,
  // so starting another stack never drops an earlier one's protection.
  const typedRef = useRef(new Set());
  const timerRef = useRef(null);
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const save = useCallback(
    (next) => {
      stacksRef.current = next;
      setPreferences?.((p) => ({ ...(p || {}), forecastStacks: next }));
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        // Best-effort and never navigates away, as with the card order.
        // telemetry-no-intent: debounced persist; the handlers below emit stack.arrange / stack.rename
        apiPatch("/api/me/preferences", { forecastStacks: next }, { onUnauthorized: () => {} });
      }, 400);
    },
    [setPreferences],
  );

  const suggestName = useCallback(
    (stackId, questions) => {
      callLlm(suggestPrompt(questions), { webSearch: false, timeoutMs: SUGGEST_TIMEOUT_MS })
        .then((raw) => {
          const name = cleanStackName(raw);
          if (!name || typedRef.current.has(stackId)) return;
          if (!stacksRef.current.some((s) => s.id === stackId)) return;
          save(renameStack(stacksRef.current, stackId, name));
        })
        // The quick guess stands; a failed suggestion is still logged and counted.
        .catch((err) => reportFailure("forecast_stacks.suggest_name_failed", err));
    },
    [save],
  );

  /**
   * Drop a card onto another: join its stack, or start a new one, named from what its
   * questions share and opened for editing. `questions` are the new stack's questions.
   * Returns whether anything changed (a full stack refuses the card).
   */
  const onStack = useCallback(
    (cardId, targetId, questions = []) => {
      const newId = `stack-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
      const { stacks: made, createdId } = stackCards(stacks, cardId, targetId, newId);
      if (made === stacks) return false;
      if (!createdId) {
        save(made);
        emitStackArrange(stackOf(made, cardId)?.id ?? null, "join");
        return true;
      }
      emitStackArrange(createdId, "create");
      const guess = stackTheme(questions);
      save(guess ? renameStack(made, createdId, guess) : made);
      setEditingId(createdId);
      if (questions.length) suggestName(createdId, questions);
      return true;
    },
    [stacks, save, suggestName],
  );
  const onUnstack = useCallback(
    (cardId) => {
      emitStackArrange(stackOf(stacks, cardId)?.id ?? null, "leave");
      save(unstackCard(stacks, cardId));
    },
    [stacks, save],
  );
  /**
   * A deleted forecast leaves its stack. Reads the SAVED stacks, not `stacks`, which is empty
   * while the rail is off: unstacking from that would save an empty list over every stack.
   */
  const onCardDeleted = useCallback(
    (cardId) => {
      const saved = sanitizeStacks(preferences?.forecastStacks);
      if (stackOf(saved, cardId)) save(unstackCard(saved, cardId));
    },
    [preferences, save],
  );
  const onMoveIntoStack = useCallback(
    (cardId, stackId, beforeId) => {
      const next = moveIntoStack(stacks, cardId, stackId, beforeId);
      if (next === stacks) return false;
      save(next);
      emitStackArrange(stackId, "move");
      return true;
    },
    [stacks, save],
  );
  const onReorderInStack = useCallback(
    (stackId, fromId, toId) => {
      emitStackArrange(stackId, "reorder");
      save(reorderInStack(stacks, stackId, fromId, toId));
    },
    [stacks, save],
  );
  // A consequence of an update or an open the user asked for, which records its own intent; no
  // stack.arrange here.
  const onPromote = useCallback(
    (cardId) => {
      const next = promoteInStack(stacksRef.current, cardId);
      if (next !== stacksRef.current) save(next);
    },
    [save],
  );
  const onRename = useCallback(
    (stackId, name) => {
      const prev = stacksRef.current;
      const next = renameStack(prev, stackId, name);
      const nameOf = (list) => list.find((s) => s.id === stackId)?.name;
      // Enter then the blur it causes both commit; only a change is a rename. Never the name.
      if (nameOf(next) !== nameOf(prev)) {
        withIntent(
          "stack.rename",
          () => undefined,
          { stack_id: stackId },
          { origin: "forecast-manager.stack-name", surface: "forecast-manager" },
        );
      }
      save(next);
      setEditingId(null);
    },
    [save],
  );
  /** Open one stack (closing any other), or close it if it is the open one. */
  const onToggle = useCallback(
    (stackId) => setExpanded((s) => (s.has(stackId) ? new Set() : new Set([stackId]))),
    [],
  );
  const onCollapseAll = useCallback(() => setExpanded((s) => (s.size ? new Set() : s)), []);

  return {
    stacks,
    expanded,
    editingId,
    onStack,
    onUnstack,
    onCardDeleted,
    onMoveIntoStack,
    onReorderInStack,
    onPromote,
    onRename,
    onStartRename: setEditingId,
    onCancelRename: () => setEditingId(null),
    onNameTyped: (stackId) => {
      typedRef.current.add(stackId);
    },
    onToggle,
    onCollapseAll,
  };
}
